import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
  chooseColor,
  ELinxSetAccountRequest,
  FlexiblePaymentMethods,
  ConnectionTypes,
  InteracWordmark,
  PaymentMethod,
  useForm,
  FieldData,
  Box,
} from 'vopay';
import GppGoodIcon from './svgs/gpp_good.svg';
import { Button } from 'UI';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useLayout } from '@/providers/Layout.provider';
import { usePaylink, Flow } from '@/providers/Paylink.provider';
import { useToken } from '@/providers/Token.provider';
import { useFlexiblePayment } from '@/providers/FlexiblePayment.provider';
import useInteracAutoDeposit from '@/hooks/useApi/useInteracAutoDeposit';
import MessageBox from '@/components/UI/MessageBox/MessageBox';
import { EmailRequest } from './types';
import { useEmailFormRows } from './buildEmailForm.helper';
import usePostLinkInterac from '@/hooks/useApi/usePostLinkInterac';
import usePostEditInterac from '@/hooks/useApi/usePostEditInterac';
import usePostPaylinkSetAccount from '@/hooks/useApi/usePostPaylinkSetAccount';
import PageTitle from '@/components/UI/PageTitle/PageTitle';
import styles from './EmailForm.module.scss';
import { useDataFetchIndicator } from '@/hooks';

type LocationProp = {
  type: 'add' | 'edit';
  selectedPaymentMethod: PaymentMethod;
  amount?: number | string;
};

const EmailForm: FunctionComponent = () => {
  const history = useHistory();
  const { state } = useLocation<LocationProp>();
  const { t } = useTranslation('translations', { keyPrefix: 'PaymentMethodForm' });
  const { setLayoutInfo } = useLayout();
  const { paylink, Token, flow } = usePaylink();

  const { token, isSetByBrowser } = useToken();
  const showBackButton = !isSetByBrowser;
  const { addPaymentMethod, paymentMethods, paymentType, partialPaymentValues, isFlexiblePayment, installmentDetails } =
    useFlexiblePayment();
  const paylinkHasName = useMemo(() => {
    return !!paylink?.ReceiverName;
  }, [paylink?.ReceiverName]);
  const { fetchAutoDepositStatus, loading } = useInteracAutoDeposit();
  const [autoDepositEnabled, setAutoDepositEnabled] = useState(false);
  const EmailFormRows = useEmailFormRows(state.type, paylinkHasName, autoDepositEnabled);
  const { linkInterac } = usePostLinkInterac();
  const { editInterac } = usePostEditInterac(token?.JWT || '');
  const [, , , completeELinx] = usePostPaylinkSetAccount({});
  const [, dataFetchIndicator] = useDataFetchIndicator();
  useEffect(() => {
    dataFetchIndicator(loading, 'loading', 'fullScreen');
  }, [loading]);
  useEffect(() => {
    setLayoutInfo(
      chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'curve',
      '',
      80,
      20,
      0,
      showBackButton || !!Token,
      true,
    );
  }, []);

  const defaultValues = {
    PaymentMethod: 'email' as const,
    PhoneNumber: paylink.ReceiverPhoneNumber,
    EmailAddress: paylink.ReceiverEmailAddress,
    Question: `What is your mother's maiden name?`,
    Answer: '',
    AccountType:
      token?.AccountHolderType === '' || token?.AccountHolderType === 'individual'
        ? ('personal' as const)
        : token?.AccountHolderType,
  };

  const fieldDataList: FieldData<EmailRequest>[] = [...EmailFormRows];

  const handleCompleteELinxResponse = async (completeElinxRequest: ELinxSetAccountRequest, values: EmailRequest) => {
    const completeELinxResponse = await completeELinx(completeElinxRequest);

    if (completeELinxResponse.Success) {
      history.push('/thank-you', {
        paymentMethodType: 'email',
        paymentMethod: values.PaymentMethod === 'email' ? values.EmailAddress : values.PhoneNumber,
        emailConnectionType: values.PaymentMethod,
        email: values.EmailAddress,
        phone: values.PhoneNumber,
      });
    } else {
      history.push('/connect/transaction-error');
    }
  };

  const addEmail = async (values: EmailRequest) => {
    const linkInteracResponse = await linkInterac({
      RecipientName: paylinkHasName
        ? paylink.ReceiverName
        : values.AccountType === 'business'
          ? `${values.CompanyName || ''}`
          : `${values.FirstName || ''} ${values.LastName || ''}`,
      RecipientEmailAddress: values.PaymentMethod === 'email' ? values.EmailAddress : '',
      RecipientPhoneNumber: values.PaymentMethod === 'phone' ? values.PhoneNumber : '',
      Question: values.Question,
      Answer: values.Answer,
    });

    if (!linkInteracResponse.Success) {
      history.push('/connect/transaction-error');
      return;
    }

    if (!Token) {
      const redirectMethod = token?.RedirectMethod;
      const url = `${token?.RedirectURL || ''}?Token=${linkInteracResponse?.Token}&PaymentMethod=email`;
      const Response = {
        Step: 'LINK',
        Url: url,
        RecipientName: paylinkHasName
          ? paylink.ReceiverName
          : values.AccountType === 'business'
            ? `${values.CompanyName || ''}`
            : `${values.FirstName || ''} ${values.LastName || ''}`,
        RecipientEmailAddress: values.PaymentMethod === 'email' ? values.EmailAddress : '',
        RecipientPhoneNumber: values.PaymentMethod === 'phone' ? values.PhoneNumber : '',
        Question: values.Question,
        Answer: values.Answer,
        PaymentMethod: 'email',
        Token: linkInteracResponse.Token,
      };
      // inner and outer redirect
      window.top?.postMessage(Response, '*');
      if (window.parent && window.parent !== window.top) {
        window.parent.postMessage(Response, '*');
      }

      setTimeout(() => {
        if (redirectMethod === 'InnerRedirect') {
          window.location.href = url;
        } else if (redirectMethod === 'OuterRedirect') {
          window.top!.location.href = url;
        } else {
          history.push('/success');
        }
      }, 300);
      return;
    }

    const defaultElinxRequest: ELinxSetAccountRequest = {
      SingleUseToken: Token,
      Iq11Token: linkInteracResponse.Token,
      PaymentMethod: 'email',
    };

    if (isFlexiblePayment && paymentType === FlexiblePaymentMethods.MultiplePaymentMethods) {
      const paymentMethod = paymentMethods.find((method) => method.method === ConnectionTypes.Email);

      addPaymentMethod({
        ...paymentMethod,
        amount: paymentMethod?.amount || 0,
        token: linkInteracResponse.Token,
        account: values.EmailAddress || values.PhoneNumber,
        method: ConnectionTypes.Email,
      });

      history.push('/connect/flexible-payment/multiple-payment-methods');
      return;
    }

    const completeElinxRequest: ELinxSetAccountRequest = isFlexiblePayment
      ? paymentType === FlexiblePaymentMethods.PartialPayment
        ? {
            ...defaultElinxRequest,
            FlexiblePaymentOptions: {
              PaymentOption: 'partial',
              PartialPaymentDetails: partialPaymentValues,
            },
          }
        : paymentType === FlexiblePaymentMethods.InstallmentPlan
          ? {
              ...defaultElinxRequest,
              FlexiblePaymentOptions: {
                PaymentOption: 'installments',
                InstallmentDetails: installmentDetails,
              },
            }
          : defaultElinxRequest
      : defaultElinxRequest;

    await handleCompleteELinxResponse(completeElinxRequest, values);
  };

  const editEmail = async (value: EmailRequest) => {
    const editInteracEmailResponse = await editInterac({
      Token: state.selectedPaymentMethod.Token,
      Question: value.Question,
      Answer: value.Answer,
    });
    if (!editInteracEmailResponse.Success) {
      history.push('/connect/transaction-error');
    } else {
      history.push({
        pathname: '/connect/payment-method/summary',
        state: {
          selectedPaymentMethod: state.selectedPaymentMethod,
          token: editInteracEmailResponse.Token,
          amount: state.amount || paylink.Amount,
        },
      });
    }
  };

  const { renderFormElements, ctx } = useForm<EmailRequest, EmailRequest>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: (serializedData) => (state.type === 'add' ? addEmail(serializedData) : editEmail(serializedData)),
  });

  //TODO: Do not check 'Auto Deposit Enabled' for collection
  useEffect(() => {
    const controller = new AbortController();

    const signal = controller.signal; // extract signal
    const debounceTimeout = setTimeout(async () => {
      if (
        ctx.values.PaymentMethod === 'email' &&
        !ctx.errors.EmailAddress &&
        (flow === Flow.WITHDRAW__SINGLE || flow === Flow.WITHDRAW__MULTIPLE_PAYMENTS || state.type === 'edit')
      ) {
        try {
          const resp = await fetchAutoDepositStatus(ctx.values.EmailAddress);
          if (!signal.aborted) {
            setAutoDepositEnabled(resp.AutoDepositEnabled);
          }
        } catch {
          setAutoDepositEnabled(false);
        }
      }
    }, 500);
    if (ctx.values.PaymentMethod === 'phone') {
      setAutoDepositEnabled(false);
    }
    return () => {
      controller.abort();
      clearTimeout(debounceTimeout);
    };
  }, [ctx.values.EmailAddress, ctx.values.PaymentMethod]);

  return (
    <div className={styles.emailFormContainer}>
      <PageTitle
        title={
          <InteracWordmark
            type={flow === Flow.FUND__SINGLE || flow === Flow.FUND__SCHEDULED ? 'moneyRequest' : 'eTransfer'}
          />
        }
      />
      <div className={styles.rows}>
        {renderFormElements({ config: EmailFormRows })}
        {autoDepositEnabled && (
          <MessageBox>
            <Box flx="row" ai="center" gapSize="16px">
              <img src={GppGoodIcon} alt="good icon" />
              <div>
                <Trans t={t} i18nKey="autoDepositMessage" components={{ bold: <span style={{ fontWeight: 600 }} /> }} />
              </div>
            </Box>
          </MessageBox>
        )}
      </div>
      <Button
        disabled={!ctx.isValid}
        className={styles.button}
        colorScheme="primary"
        onClick={() => {
          ctx.handleSubmit();
        }}
      >
        {t('confirmButton')}
      </Button>
    </div>
  );
};

export default EmailForm;
